import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useState } from 'preact/hooks';
import { Plus, Calendar, Mail, Phone, MessageSquare, Hash, Pencil, Trash2, AlertCircle, Loader2, ChevronDown, ChevronRight } from 'lucide-preact';
import { TOUCHPOINT_TYPE_LABELS } from '@/types/touchPoint';
import { TouchPointModal } from '../modals/TouchPointModal';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { useLookups } from '@/hooks/useLookups';
import { useTouchPointManager } from '@/hooks/useTouchPointManager';
import { useTouchPointGroups } from '@/hooks/useTouchPointGroups';
import { buildTouchPointCreateDto, buildTouchPointUpdateDto } from '@/utils/touchPointDtos';
export function WebActivationsTab({ web, workspaceId, campaignId }) {
    // Use the shared touchpoint manager hook
    const { touchPoints, loading, error, saving, deleting, fetchTouchPoints, createTouchPoint, updateTouchPoint, deleteTouchPoint, } = useTouchPointManager({
        workspaceId,
        campaignId,
        webId: web.Id,
        typeFilters: ['Email', 'Phone', 'Social'],
        isCampaignLevel: false,
    });
    // Use the shared grouping hook
    const { groupedTouchPoints, toggleGroup, isGroupExpanded } = useTouchPointGroups({
        touchPoints,
        groupBy: (tp) => tp.Type || 'Unknown',
    });
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [editingTouchPoint, setEditingTouchPoint] = useState(null);
    // Delete confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingTouchPoint, setDeletingTouchPoint] = useState(null);
    const lookups = useLookups();
    // Get cached touchpoint types from the hook (exclude Meeting since it's handled in a separate tab)
    const touchPointTypes = lookups.getTouchPointTypes()
        .filter(x => x.Key !== 'Meeting')
        .map(x => ({ Id: x.Key, DisplayName: x.Description }));
    const handleCreate = () => {
        setEditingTouchPoint(null);
        setShowModal(true);
    };
    const handleEdit = (touchPoint) => {
        setEditingTouchPoint(touchPoint);
        setShowModal(true);
    };
    const handleCloseModal = () => {
        if (!saving) {
            setShowModal(false);
            setEditingTouchPoint(null);
        }
    };
    const handleSave = async (formData) => {
        if (editingTouchPoint) {
            // Update existing touchpoint
            const updateDto = buildTouchPointUpdateDto(formData, editingTouchPoint.ETag);
            const result = await updateTouchPoint(editingTouchPoint.Id, updateDto, 'Activation Updated');
            if (result.success) {
                setShowModal(false);
                setEditingTouchPoint(null);
            }
        }
        else {
            // Create new touchpoint
            const createDto = buildTouchPointCreateDto(formData, campaignId, web.Id);
            const result = await createTouchPoint(createDto, 'Activation Created');
            if (result.success) {
                setShowModal(false);
                setEditingTouchPoint(null);
            }
        }
    };
    const handleDeleteClick = (touchPoint) => {
        setDeletingTouchPoint(touchPoint);
        setShowDeleteConfirm(true);
    };
    const handleDeleteConfirm = async () => {
        if (!deletingTouchPoint)
            return;
        const result = await deleteTouchPoint(deletingTouchPoint.Id, deletingTouchPoint.Name, 'Activation Deleted');
        if (result.success) {
            setShowDeleteConfirm(false);
            setDeletingTouchPoint(null);
        }
    };
    const handleDeleteCancel = () => {
        if (!deleting) {
            setShowDeleteConfirm(false);
            setDeletingTouchPoint(null);
        }
    };
    const getTypeIcon = (type) => {
        switch (type) {
            case 'Email':
                return _jsx(Mail, { className: "w-5 h-5" });
            case 'Phone':
                return _jsx(Phone, { className: "w-5 h-5" });
            case 'Social':
                return _jsx(MessageSquare, { className: "w-5 h-5" });
            case 'Sms':
                return _jsx(Hash, { className: "w-5 h-5" });
            default:
                return _jsx(Calendar, { className: "w-5 h-5" });
        }
    };
    const getTypeColor = (type) => {
        switch (type) {
            case 'Email':
                return {
                    bg: 'bg-blue-100 dark:bg-blue-900/30',
                    text: 'text-blue-700 dark:text-blue-300',
                    border: 'border-blue-200 dark:border-blue-800',
                    icon: 'text-blue-600 dark:text-blue-400'
                };
            case 'Phone':
                return {
                    bg: 'bg-emerald-100 dark:bg-emerald-900/30',
                    text: 'text-emerald-700 dark:text-emerald-300',
                    border: 'border-emerald-200 dark:border-emerald-800',
                    icon: 'text-emerald-600 dark:text-emerald-400'
                };
            case 'Social':
                return {
                    bg: 'bg-purple-100 dark:bg-purple-900/30',
                    text: 'text-purple-700 dark:text-purple-300',
                    border: 'border-purple-200 dark:border-purple-800',
                    icon: 'text-purple-600 dark:text-purple-400'
                };
            case 'Sms':
                return {
                    bg: 'bg-orange-100 dark:bg-orange-900/30',
                    text: 'text-orange-700 dark:text-orange-300',
                    border: 'border-orange-200 dark:border-orange-800',
                    icon: 'text-orange-600 dark:text-orange-400'
                };
            default:
                return {
                    bg: 'bg-slate-100 dark:bg-slate-900/30',
                    text: 'text-slate-700 dark:text-slate-300',
                    border: 'border-slate-200 dark:border-slate-800',
                    icon: 'text-slate-600 dark:text-slate-400'
                };
        }
    };
    const formatDateTime = (isoString) => {
        try {
            const date = new Date(isoString);
            // Use UTC methods since we store dates at UTC midnight
            return date.toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                timeZone: 'UTC',
            });
        }
        catch (e) {
            return isoString;
        }
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-6 shadow-sm", children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsxs("div", { children: [_jsx("h2", { className: "text-lg font-semibold text-slate-900 dark:text-white", children: "Activations" }), _jsx("p", { className: "text-sm text-slate-600 dark:text-slate-400 mt-1", children: "Schedule and manage campaign touchpoints for this web" })] }), _jsxs("button", { onClick: handleCreate, disabled: loading, className: "flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed", children: [_jsx(Plus, { className: "w-4 h-4" }), _jsx("span", { children: "New Activation" })] })] }), loading && (_jsx("div", { className: "flex items-center justify-center py-12", children: _jsx(Loader2, { className: "w-6 h-6 text-primary-600 animate-spin" }) })), error && !loading && (_jsx("div", { className: "rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4", children: _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx(AlertCircle, { className: "w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" }), _jsxs("div", { children: [_jsx("h3", { className: "text-sm font-medium text-red-800 dark:text-red-300", children: "Failed to load activations" }), _jsx("p", { className: "text-sm text-red-700 dark:text-red-400 mt-1", children: error }), _jsx("button", { onClick: fetchTouchPoints, className: "mt-2 text-sm font-medium text-red-700 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 underline", children: "Try again" })] })] }) })), !loading && !error && touchPoints.length === 0 && (_jsxs("div", { className: "text-center py-12", children: [_jsx(Calendar, { className: "w-12 h-12 text-slate-400 dark:text-slate-600 mx-auto mb-3" }), _jsx("p", { className: "text-sm text-slate-600 dark:text-slate-400", children: "No activations yet. Create your first touchpoint to get started." })] })), !loading && !error && touchPoints.length > 0 && (_jsx("div", { className: "space-y-3", children: Object.entries(groupedTouchPoints)
                            .sort(([typeA], [typeB]) => typeA.localeCompare(typeB))
                            .map(([type, items]) => {
                            const colors = getTypeColor(type);
                            const isExpanded = isGroupExpanded(type);
                            return (_jsxs("div", { className: `rounded-lg border ${colors.border} ${colors.bg} overflow-hidden`, children: [_jsxs("button", { onClick: () => toggleGroup(type), className: "w-full flex items-center justify-between p-4 hover:opacity-80 transition-opacity", children: [_jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: colors.icon, children: getTypeIcon(type) }), _jsx("h3", { className: `text-base font-semibold ${colors.text}`, children: TOUCHPOINT_TYPE_LABELS[type] || type }), _jsx("span", { className: `px-2.5 py-0.5 text-sm font-medium rounded-full ${colors.bg} ${colors.text} border ${colors.border}`, children: items.length })] }), _jsx("div", { className: colors.icon, children: isExpanded ? (_jsx(ChevronDown, { className: "w-5 h-5" })) : (_jsx(ChevronRight, { className: "w-5 h-5" })) })] }), isExpanded && (_jsx("div", { className: "border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800", children: _jsx("div", { className: "p-3 space-y-2", children: items
                                                .sort((a, b) => {
                                                // Get scheduled date from any type
                                                const aDate = a.EmailProperties?.ScheduledDate || a.PhoneProperties?.ScheduledDate || a.SocialProperties?.ScheduledDate || '';
                                                const bDate = b.EmailProperties?.ScheduledDate || b.PhoneProperties?.ScheduledDate || b.SocialProperties?.ScheduledDate || '';
                                                return new Date(aDate).getTime() - new Date(bDate).getTime();
                                            })
                                                .map((tp) => (_jsxs("div", { className: "flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 hover:border-primary-300 dark:hover:border-primary-700 transition-colors", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h4", { className: "text-sm font-medium text-slate-900 dark:text-slate-100 truncate", children: tp.Name }), _jsxs("div", { className: "flex items-center space-x-4 mt-1", children: [(tp.EmailProperties?.ScheduledDate || tp.PhoneProperties?.ScheduledDate || tp.SocialProperties?.ScheduledDate) && (_jsxs("div", { className: "flex items-center space-x-1.5 text-xs text-slate-600 dark:text-slate-400", children: [_jsx(Calendar, { className: "w-3.5 h-3.5" }), _jsx("span", { children: formatDateTime(tp.EmailProperties?.ScheduledDate ||
                                                                                    tp.PhoneProperties?.ScheduledDate ||
                                                                                    tp.SocialProperties?.ScheduledDate ||
                                                                                    '') })] })), tp.EmailProperties && (_jsx("div", { className: "text-xs text-slate-500 dark:text-slate-500 truncate max-w-xs", children: tp.EmailProperties.Subject })), tp.PhoneProperties && (_jsxs("div", { className: "text-xs text-slate-500 dark:text-slate-500", children: [tp.PhoneProperties.NumberOfDials, " ", tp.PhoneProperties.NumberOfDials === 1 ? 'dial' : 'dials'] })), tp.SocialProperties && (_jsx("div", { className: "text-xs text-slate-500 dark:text-slate-500", children: tp.SocialProperties.Platform }))] })] }), _jsxs("div", { className: "flex items-center space-x-2 ml-4", children: [_jsx("button", { onClick: () => handleEdit(tp), className: "p-2 text-slate-600 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors", title: "Edit", children: _jsx(Pencil, { className: "w-4 h-4" }) }), _jsx("button", { onClick: () => handleDeleteClick(tp), className: "p-2 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors", title: "Delete", children: _jsx(Trash2, { className: "w-4 h-4" }) })] })] }, tp.Id))) }) }))] }, type));
                        }) }))] }), showModal && (_jsx(TouchPointModal, { touchPoint: editingTouchPoint, touchPointTypes: touchPointTypes, onClose: handleCloseModal, onSave: handleSave, saving: saving, workspaceId: workspaceId, campaignId: campaignId })), _jsx(ConfirmationModal, { isOpen: showDeleteConfirm, title: "Delete Activation", message: `Are you sure you want to delete "${deletingTouchPoint?.Name}"? This action cannot be undone.`, type: "danger", confirmText: "Delete", onConfirm: handleDeleteConfirm, onCancel: handleDeleteCancel, loading: deleting })] }));
}
